/**
 * Authentication error codes used throughout the AuthProvider system.
 * These codes help identify specific failure points in the authentication flow.
 */
export const AUTH_ERROR_CODES = {
  /**
   * Authorization callback errors (AP-1, AP-2)
   */
  AUTHORIZATION_TOKEN_MISSING: "AP-1",
  AUTHORIZATION_VERIFICATION_FAILED: "AP-2",

  /**
   * Token refresh errors (AP-R1, AP-R2)
   */
  TOKEN_DECODE_FAILED: "AP-R1",
  TOKEN_REFRESH_FAILED: "AP-R2",

  /**
   * Email verification resend errors (AP-R3, AP-R4, AP-R5)
   */
  VERIFICATION_EMAIL_REQUEST_FAILED: "AP-R3",
  VERIFICATION_EMAIL_NO_AUTH_ID: "AP-R4",
  VERIFICATION_EMAIL_SERVER_ERROR: "AP-R5",
} as const;

/**
 * Type representing all possible auth error code values
 */
export type AuthErrorCode =
  (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

/**
 * Detailed error information for each error code
 */
export const AUTH_ERROR_DETAILS: Record<
  AuthErrorCode,
  {
    code: AuthErrorCode;
    category: "authorization" | "token_refresh" | "email_verification";
    description: string;
    userMessage: string;
  }
> = {
  [AUTH_ERROR_CODES.AUTHORIZATION_TOKEN_MISSING]: {
    code: AUTH_ERROR_CODES.AUTHORIZATION_TOKEN_MISSING,
    category: "authorization",
    description:
      "Failed to retrieve identity token from authorization response",
    userMessage: "Login failed. Please try again.",
  },
  [AUTH_ERROR_CODES.AUTHORIZATION_VERIFICATION_FAILED]: {
    code: AUTH_ERROR_CODES.AUTHORIZATION_VERIFICATION_FAILED,
    category: "authorization",
    description: "Failed to verify OAuth login token during authorization",
    userMessage: "Login verification failed. Please try again.",
  },
  [AUTH_ERROR_CODES.TOKEN_DECODE_FAILED]: {
    code: AUTH_ERROR_CODES.TOKEN_DECODE_FAILED,
    category: "token_refresh",
    description: "Failed to decode identity token during refresh process",
    userMessage: "Session refresh failed. Please log in again.",
  },
  [AUTH_ERROR_CODES.TOKEN_REFRESH_FAILED]: {
    code: AUTH_ERROR_CODES.TOKEN_REFRESH_FAILED,
    category: "token_refresh",
    description: "Failed to refresh identity token from server",
    userMessage: "Session refresh failed. Please log in again.",
  },
  [AUTH_ERROR_CODES.VERIFICATION_EMAIL_REQUEST_FAILED]: {
    code: AUTH_ERROR_CODES.VERIFICATION_EMAIL_REQUEST_FAILED,
    category: "email_verification",
    description:
      "Network or request error when attempting to resend verification email",
    userMessage: "Failed to send verification email. Please try again.",
  },
  [AUTH_ERROR_CODES.VERIFICATION_EMAIL_NO_AUTH_ID]: {
    code: AUTH_ERROR_CODES.VERIFICATION_EMAIL_NO_AUTH_ID,
    category: "email_verification",
    description:
      "No authentication ID available for resending verification email",
    userMessage: "Unable to resend verification email. Please register again.",
  },
  [AUTH_ERROR_CODES.VERIFICATION_EMAIL_SERVER_ERROR]: {
    code: AUTH_ERROR_CODES.VERIFICATION_EMAIL_SERVER_ERROR,
    category: "email_verification",
    description:
      "Server returned failure response when resending verification email",
    userMessage: "Failed to send verification email. Please try again later.",
  },
};

/**
 * Helper function to get error details by code
 * @param code - The auth error code
 * @returns Error details object or undefined if code not found
 */
export const getAuthErrorDetails = (code: string) =>
  AUTH_ERROR_DETAILS[code as AuthErrorCode];

/**
 * Helper function to check if a string is a valid auth error code
 * @param code - The code to validate
 * @returns True if the code is a valid auth error code
 */
export const isValidAuthErrorCode = (code: string): code is AuthErrorCode =>
  Object.values(AUTH_ERROR_CODES).includes(code as AuthErrorCode);
